import { Capacitor, registerPlugin } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'

export interface SharedContent {
  type: 'text' | 'image' | 'none'
  text?: string
  subject?: string
  uri?: string
}

interface ShareReceiverPlugin {
  getSharedData(): Promise<SharedContent>
  clearSharedData(): Promise<void>
}

const ShareReceiver = registerPlugin<ShareReceiverPlugin>('ShareReceiver')

const IOS_SHARED_DATA_KEY = 'SharedQuickCapture'

/**
 * Check if shared content is available from an external app's share action.
 * Android: uses the custom ShareReceiver Capacitor plugin (reads intent extras).
 * iOS: reads from App Group UserDefaults (written by the Share Extension).
 * Web: always returns { type: 'none' }.
 */
export async function getSharedContent(): Promise<SharedContent> {
  if (!Capacitor.isNativePlatform()) {
    return { type: 'none' }
  }

  const platform = Capacitor.getPlatform()

  if (platform === 'android') {
    return ShareReceiver.getSharedData()
  }

  if (platform === 'ios') {
    return getIOSSharedContent()
  }

  return { type: 'none' }
}

/**
 * Clear the shared content so it doesn't re-trigger on next app resume.
 */
export async function clearSharedContent(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return

  const platform = Capacitor.getPlatform()

  if (platform === 'android') {
    await ShareReceiver.clearSharedData()
  }

  if (platform === 'ios') {
    // Clear the App Group UserDefaults entry
    await Preferences.remove({ key: IOS_SHARED_DATA_KEY })
  }
}

/**
 * iOS: read shared data from App Group UserDefaults.
 * The Share Extension writes a JSON dict to UserDefaults under the app group.
 * Capacitor's Preferences plugin doesn't support app groups natively,
 * so we use a well-known convention and clear via Preferences after reading.
 */
async function getIOSSharedContent(): Promise<SharedContent> {
  try {
    // Try reading from Preferences (Capacitor stores in UserDefaults)
    const result = await Preferences.get({ key: IOS_SHARED_DATA_KEY })
    if (result.value) {
      const data = JSON.parse(result.value) as SharedContent
      return data
    }
  } catch {
    // Silently fail — no shared data
  }
  return { type: 'none' }
}
